import Phaser from 'phaser';

import * as C from 'tower/constants';
import gameState from 'tower/state/game-state';
import { makeStandardController, StandardController, State, StateMachine } from 'tower/util';
import BattleScene from './battle';
import BaseScene from './base';

export default class HUDScene extends BaseScene {
  background!: Phaser.GameObjects.Rectangle;
  lifeText!: Phaser.GameObjects.BitmapText;
  buttonsHUD!: Phaser.GameObjects.Image;
  swordImage!: Phaser.GameObjects.Image;
  scoreText!: Phaser.GameObjects.BitmapText;
  keyText!: Phaser.GameObjects.BitmapText;
  roomSolvedText!: Phaser.GameObjects.BitmapText;
  subweaponImage!: Phaser.GameObjects.Image;
  items!: Phaser.GameObjects.Image[];
  controller!: StandardController;
  stateMachine!: StateMachine<PlayingState | PausedState>;
  cursor!: Phaser.GameObjects.Image;

  constructor(config: Phaser.Types.Scenes.SettingsConfig = {}) {
    super({
      key: 'hud',
      ...config,
    });
  }

  loadResources(scene: Phaser.Scene) {
    scene.load.spritesheet('items', 'img/items.png', { frameWidth: 16, frameHeight: 16 });
    scene.load.image('buttonsHUD', 'img/buttons_hud.png');
    scene.load.spritesheet('cursor', 'img/cursor.png', { frameWidth: 16, frameHeight: 16 });

    scene.load.bitmapFont('textWhite', 'fonts/text_white.png', 'fonts/text.fnt');
    scene.load.bitmapFont('textRed', 'fonts/text_red.png', 'fonts/text.fnt');

    scene.load.audio('pause', 'audio/pause.mp3');
    scene.load.audio('select', 'audio/select.mp3');
  }

  create() {
    this.controller = makeStandardController(this);

    this.anims.create({
      key: 'cursor',
      frameRate: 8,
      frames: this.anims.generateFrameNumbers('cursor', {
        frames: [0, 1],
      }),
      repeat: -1,
    });

    this.background = this.add.rectangle(C.GAME_WIDTH / 2, C.GAME_HEIGHT - 16, C.GAME_WIDTH, 32, 0x000000);
    this.lifeText = this.add.bitmapText(8, 224, 'textRed', this.makeLifeText(gameState.hp, gameState.maxHp));
    this.scoreText = this.add.bitmapText(208, 224, 'textWhite', this.makeScoreText(gameState.score));
    this.roomSolvedText = this.add.bitmapText(
      208,
      232,
      'textBlue',
      this.makeSolvedText(gameState.isRoomSolved(gameState.currentRoomKey)),
    );
    this.keyText = this.add.bitmapText(224, 232, 'textWhite', this.makeKeyText(gameState.keyCount, gameState.items));
    this.buttonsHUD = this.add.image(88, 240, 'buttonsHUD');
    this.swordImage = this.add.image(100, 240, 'items', 1);
    this.subweaponImage = this.add.image(
      76,
      240,
      'items',
      this.getWeaponFrame(gameState.subweapon, gameState.subweaponsDisabled),
    );
    this.cursor = this.add.sprite(0, 0, 'cursor').play('cursor').setVisible(false);

    this.items = [];
    for (let k = 0; k < 12; k++) {
      const item = this.add.image(120 + (k % 6) * 16, 232 + (k >= 6 ? 16 : 0), 'items', 8);
      this.items.push(item);
    }

    this.addGameStateHandler('healthchange', (hp: number, maxHp: number) => {
      this.lifeText.setText(this.makeLifeText(hp, maxHp));
    });

    this.addGameStateHandler('itemchange', (items: Set<string>) => {
      this.syncItems(items);
      this.subweaponImage.setFrame(this.getWeaponFrame(gameState.subweapon, gameState.subweaponsDisabled));
      this.lifeText.setText(this.makeLifeText(gameState.hp, gameState.maxHp));
    });

    this.addGameStateHandler('scorechange', (score: number) => {
      this.scoreText.setText(this.makeScoreText(score));
    });

    this.addGameStateHandler('subweaponchange', (subweapon: C.ItemKey) => {
      this.subweaponImage.setFrame(this.getWeaponFrame(subweapon, gameState.subweaponsDisabled));
    });

    this.addGameStateHandler('mattockdurabilitychange', () => {
      // Refresh mattock sprite in case it is equipped
      this.subweaponImage.setFrame(this.getWeaponFrame(gameState.subweapon, gameState.subweaponsDisabled));
      this.syncItems(gameState.items);
    });

    this.addGameStateHandler('candledurabilitychange', () => {
      // Refresh candle sprite in case it is equipped
      this.subweaponImage.setFrame(this.getWeaponFrame(gameState.subweapon, gameState.subweaponsDisabled));
      this.syncItems(gameState.items);
    });

    this.addGameStateHandler('subweaponsdisabledchanged', () => {
      this.subweaponImage.setFrame(this.getWeaponFrame(gameState.subweapon, gameState.subweaponsDisabled));
    });

    this.addGameStateHandler('keycountchange', (keyCount: number) => {
      this.keyText.setText(this.makeKeyText(keyCount, gameState.items));
    });

    this.addGameStateHandler('currentroomkeychanged', (roomKey: string) => {
      this.roomSolvedText.setText(this.makeSolvedText(gameState.isRoomSolved(roomKey)));
    });

    this.addGameStateHandler('currentroomkeychanged', (roomKey: string) => {
      this.roomSolvedText.setText(this.makeSolvedText(gameState.isRoomSolved(roomKey)));
    });

    this.addGameStateHandler('solvedroomschange', (solvedRooms: Set<string>) => {
      this.roomSolvedText.setText(this.makeSolvedText(solvedRooms.has(gameState.currentRoomKey)));
    });

    this.syncItems(gameState.items);

    this.stateMachine = new StateMachine('playing', {
      playing: new PlayingState(this),
      paused: new PausedState(this),
    });
  }

  addGameStateHandler(event: string, handler: Function) {
    gameState.addListener(event, handler);
    this.events.once('shutdown', () => gameState.removeListener(event, handler));
  }

  syncItems(items: Set<string>) {
    if (gameState.sword !== null) {
      this.swordImage.setFrame(C.ITEMS[gameState.sword].getInventoryFrame(gameState));
    }

    this.keyText.setText(this.makeKeyText(gameState.keyCount, items));

    for (const item of this.items) {
      item.setFrame(0).setData('itemKey', null);
    }

    let itemIndex = 0;
    for (const itemKey of C.INVENTORY_DISPLAY_ITEMS) {
      if (items.has(itemKey)) {
        const item = this.items[itemIndex++];
        item.setFrame(C.ITEMS[itemKey].getInventoryFrame(gameState)).setData('itemKey', itemKey);
      }
    }
  }

  makeScoreText(score: number) {
    return score.toString().padStart(5, '0');
  }

  makeKeyText(keyCount: number, items: Set<string>) {
    if (items.has('magicKey')) {
      return 'eXA';
    }

    return `eX${keyCount}`;
  }

  makeSolvedText(roomSolved: boolean) {
    return roomSolved ? 'l' : 'k';
  }

  makeLifeText(hp: number, maxHp: number) {
    let text = '-LIFE-\n';
    for (let k = 0; k < maxHp; k += 2) {
      if (k === 12) {
        text += '\n';
      }
      if (hp >= k + 2) {
        text += 'c';
      } else if (hp === k + 1) {
        text += 'b';
      } else {
        text += 'a';
      }
    }
    return text;
  }

  getWeaponFrame(key: C.ItemKey | null, subweaponsDisabled: boolean) {
    if (subweaponsDisabled) {
      return 53;
    }

    return key === null ? 0 : (C.ITEMS[key].getSubweaponFrame?.(gameState) ?? 0);
  }

  getItemImage(key: string | null) {
    for (const item of this.items) {
      if (item.getData('itemKey') === key) {
        return item;
      }
    }

    return null;
  }

  update() {
    this.stateMachine.update();
  }
}

class HUDState extends State {
  constructor(protected scene: HUDScene) {
    super();
  }
}

class PlayingState extends HUDState {
  handleEntered() {
    this.scene.scene.resume('battle');
  }

  update() {
    const { scene } = this;

    // Can only pause if game is in playing state
    const battleScene = scene.scene.get('battle') as BattleScene;
    if (battleScene.stateMachine.state !== 'playing') {
      return;
    }

    if (scene.controller.start.justDown) {
      scene.sound.play('pause');
      return this.transition('paused');
    }
  }
}

interface SelectableSubweapon {
  key: C.ItemKey;
  image: Phaser.GameObjects.Image;
}

class PausedState extends HUDState {
  cursorDisabled!: boolean;
  subweapons!: SelectableSubweapon[];
  cursorIndex = 0;

  handleEntered() {
    const { scene } = this;
    scene.scene.pause('battle');

    // List of subweapons that can be selected
    this.subweapons = scene.items
      .filter((i) => C.ITEMS[i.getData('itemKey') as C.ItemKey]?.isSubweapon)
      .map((i) => ({
        key: i.getData('itemKey') as C.ItemKey,
        image: i,
      }));

    // Disable cursor if nothing is equipable
    if (this.subweapons.length < 1) {
      this.cursorDisabled = true;
      return;
    }

    const subweaponIndex = this.subweapons.findIndex((s) => s.key === gameState.subweapon);
    if (subweaponIndex === -1) {
      this.cursorDisabled = true;
      return;
    }

    const itemImage = this.subweapons[subweaponIndex].image;
    this.cursorDisabled = false;
    this.cursorIndex = subweaponIndex;
    scene.cursor.setVisible(true).setPosition(itemImage.x, itemImage.y);
  }

  update() {
    const { scene } = this;

    if (scene.controller.start.justDown) {
      scene.sound.play('pause');
      return this.transition('playing');
    }

    if (!this.cursorDisabled) {
      if (scene.controller.left.justDown) {
        this.moveCursor(-1);
        scene.sound.play('select');
      } else if (scene.controller.right.justDown) {
        this.moveCursor(1);
        scene.sound.play('select');
      }
    }
  }

  moveCursor(mod: number) {
    const { scene } = this;

    this.cursorIndex += mod;
    if (this.cursorIndex < 0) {
      this.cursorIndex = this.subweapons.length - 1;
    } else if (this.cursorIndex > this.subweapons.length - 1) {
      this.cursorIndex = 0;
    }

    const { key, image } = this.subweapons[this.cursorIndex];
    scene.cursor.setPosition(image.x, image.y);
    gameState.subweapon = key;
  }

  handleExited() {
    this.scene.cursor.setVisible(false);
  }
}
